import { CSSProperties, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";
import { SpecialOrderService } from "../../../services/specialOrder.service";
import { BankCardsServices } from "../../../services/bankCards.service";
import { BankCardType } from "../../../interface/site.interface";
import Spinner from "../../../components/shared/spinner/Spinner";
import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import CardSelectModal from "../../../components/shared/card-select-modal/CardSelectModal";
import PaymentStatusModal from "../../../components/shared/payment-status-modal/PaymentStatusModal";
import NoCardsModal from "../../../components/shared/no-cards-modal/NoCardsModal";
import GlobalModalComponent from "../../../components/shared/globalModal/GlobalModalComponent";
import { toast } from "react-toastify";
import styles from "./SpecialOrder.module.scss";
import { formatDate } from "../../../extentions/date";
import deleteBtn from "../../../assets/images/otp/delete-2-svgrepo-com.svg";
import eyeIcon from "../../../assets/images/courier/Eye (1).svg";
import { Base_Url } from "../../../constants/base_url";
import ArrowImage from "../../../assets/images/pages/login/CaretLeft.svg";
import SelectComponent from "../../../components/shared/select/SelectComponent";
import { NumberPrefix } from "../../../services/phonePrefix.service";

interface Country {
  id: number;
  country_name: string;
  image?: string;
  flag?: string;
  currency_name?: string;
  special_order_commission?: number;
  special_order_partner_access?: number;
  special_order_payment_access?: number;
  special_order_minimum_cost_limit?: number | null;
  max_amount_local?: number | null;
}

interface OrderItem {
  key: string;
  link: string;
  price: string;
  count: string;
  color: string;
  size: string;
  details: string;
}

const emptyItem = (): OrderItem => ({
  key: Math.floor(10000000 + Math.random() * 90000000).toString(),
  link: "",
  price: "",
  count: "1",
  color: "",
  size: "",
  details: "",
});

interface SpecialOrder {
  id: number;
  price?: string;
  price_azn?: string;
  currency?: string;
  total_amount?: string;
  total_amount_azn?: string | null;
  status?: string;
  is_paid?: number;
  urls?: string;
  disable?: number;
  cargo_debt?: string;
  common_debt?: string;
  waiting_for_payment?: number;
  group_code?: string;
  special_order_payment_link?: string | null;
  partner_create_status?: number;
  edit_requested?: number;
  edit_reason?: string | null;
  orderNumber?: string | null;
}

interface EditItem {
  id: number;
  url: string;
  price: string;
  count: string;
  color: string;
  size: string;
  description: string;
  cargo_price: string;
}

interface BalanceLog {
  id: number;
  order_group_id?: number | null;
  order_id?: number | null;
  order_number?: string | null;
  amount: string;
  currency?: string | null;
  amount_azn?: string | null;
  rate?: string | null;
  rate_date?: string | null;
  balance_before?: string | null;
  balance_after?: string | null;
  status: "in" | "out";
  type: string;
  reason?: string | null;
  actor?: "client" | "staff" | "system";
  created_at?: string;
}

interface GroupOrderItem {
  url: string;
  price: string;
  quantity: number;
  description?: string | null;
  status?: string;
  cargo_price?: string | number | null;
}

const SpecialOrder = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data } = useSelector((state: RootState) => state.translationData);
  const language = useSelector((state: RootState) => state.language.language);
  const initialRender = useRef(true);

  // Countries
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  interface CalcItem {
    key: string;
    subtotal_with_commission: number;
  }
  interface CalcResult {
    commission_percent?: number;
    currency?: string;
    currency_azn?: string;
    total_with_commission?: number;
    total_with_commission_azn?: number;
    items?: CalcItem[];
    spain_cargo_price?: number | null;
    balance_preview?: {
      balance_azn: number;
      from_balance_azn: number;
      debt_azn: number;
      debt: number;
      balance_after: number;
    } | null;
  }

  // Form
  const [items, setItems] = useState<OrderItem[]>([emptyItem()]);
  const [calcResult, setCalcResult] = useState<CalcResult | null>(null);
  const [serviceFeePercent, setServiceFeePercent] = useState<number | null>(null);
  const [orders, setOrders] = useState<SpecialOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [archiveFilter, setArchiveFilter] = useState<"yes" | "no">("no");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [phonePrefix, setPhonePrefix] = useState<string>("50");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [phonePrefixes, setPhonePrefixes] = useState<{ id: string; prefix: string }[]>([]);

  // Balance
  const [balance, setBalance] = useState<string | null>(null);
  const [balanceCurrency, setBalanceCurrency] = useState<string>("");
  const [logsModal, setLogsModal] = useState(false);
  const [logs, setLogs] = useState<BalanceLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Payment
  const [cards, setCards] = useState<BankCardType[]>([]);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
  const [isPaymentStatusOpen, setIsPaymentStatusOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(null);
  const [isNoCardsModalOpen, setIsNoCardsModalOpen] = useState(false);

  // Payment method selection
  const [payMethodModal, setPayMethodModal] = useState(false);

  // Group orders modal
  const [groupOrdersModal, setGroupOrdersModal] = useState(false);
  const [groupOrders, setGroupOrders] = useState<GroupOrderItem[]>([]);
  const [groupOrdersLoading, setGroupOrdersLoading] = useState(false);
  const [groupOrdersReason, setGroupOrdersReason] = useState<string | null>(null);

  // Terms modal
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsConfirmMode, setTermsConfirmMode] = useState(false);

  // Partner edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editOrderId, setEditOrderId] = useState<number | null>(null);
  const [editItems, setEditItems] = useState<EditItem[]>([]);
  const [editReason, setEditReason] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editCancelling, setEditCancelling] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [editCalcResult, setEditCalcResult] = useState<CalcResult | null>(null);

  const calculateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editCalculateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedCountry = countries.find((c) => c.id === selectedCountryId) ?? null;

  const handleSelectCountry = (id: number) => {
    setSelectedCountryId(id);
    setCountryModalOpen(false);
  };

  const navigate = useNavigate();

  // Ölkə seçmədən çıxmaq: gəldiyi səhifəyə qayıt, tarixçə yoxdursa bağlamalara
  const handleCountryModalBack = () => {
    setCountryModalOpen(false);
    if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate("/account/my-packages");
    }
  };

  useEffect(() => {
    if (!countryModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCountryModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [countryModalOpen]);
  const isPartnerAccess = selectedCountry?.special_order_partner_access === 1;
  const isPaymentAccess = selectedCountry?.special_order_payment_access !== 0;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) dispatch(fetchTranslation());
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  // Fetch balance on mount
  useEffect(() => {
    SpecialOrderService.getBalance().then((res) => {
      const b = res.data?.balance ?? res.data?.data?.balance ?? null;
      const c = res.data?.currency ?? res.data?.data?.currency ?? "AZN";
      if (b !== null) { setBalance(String(b)); setBalanceCurrency(c); }
    });
  }, []);

  // Fetch phone prefixes on mount
  useEffect(() => {
    NumberPrefix.getAllPrefix()
      .then((res) => setPhonePrefixes(res.data.data || []))
      .catch(() => {});
  }, []);

  const handleOpenLogs = async () => {
    setLogsModal(true);
    setLogsLoading(true);
    setLogs([]);
    try {
      const res = await SpecialOrderService.getBalanceLogs();
      setLogs(res.data?.logs?.data ?? res.data?.logs ?? res.data?.data ?? []);
    } catch { setLogs([]); }
    finally { setLogsLoading(false); }
  };

  // Fetch countries on mount
  useEffect(() => {
    SpecialOrderService.getCountries()
      .then((res) => {
        const list: Country[] = res.data?.countries || [];
        setCountries(list);
        // Ölkə avtomatik seçilmir — istifadəçi modaldan seçməlidir
        if (list.length > 0) setCountryModalOpen(true);
      })
      .catch(() => setCountries([]))
      .finally(() => setCountriesLoading(false));
  }, []);

  // When selected country changes, fetch settings and orders
  useEffect(() => {
    if (!selectedCountryId) return;
    setServiceFeePercent(null);
    setCalcResult(null);
    setItems([emptyItem()]);
    setErrors({});
    setPhonePrefix("50");
    setPhoneNumber("");
    setCurrentPage(1);
    setLastPage(1);

    SpecialOrderService.getSettings(selectedCountryId).then((res) => {
      const fee = res.data?.service_fee ?? res.data?.data?.service_fee ?? null;
      if (fee !== null) setServiceFeePercent(Number(fee));
    });

    setArchiveFilter("no");
    fetchOrders(selectedCountryId);
  }, [selectedCountryId]);

  useEffect(() => {
    if (selectedCountryId) fetchOrders(selectedCountryId, 1, archiveFilter ?? undefined);
  }, [archiveFilter]);

  const fetchOrders = async (countryId: number, page = 1, archive?: string) => {
    setOrdersLoading(true);
    try {
      const res = await SpecialOrderService.getOrders(countryId, undefined, page, archive ?? archiveFilter ?? undefined);
      const list = res.data?.orders || [];
      setOrders(Array.isArray(list) ? list : []);
      const pagination = res.data?.pagination;
      if (pagination) {
        setCurrentPage(pagination.current_page);
        setLastPage(pagination.last_page);
      }
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const triggerCalculate = (updatedItems: OrderItem[]) => {
    if (!selectedCountryId) return;
    if (calculateTimer.current) clearTimeout(calculateTimer.current);
    calculateTimer.current = setTimeout(async () => {
      const calcItems = updatedItems
        .filter((i) => i.price && parseFloat(i.price) > 0)
        .map((item) => ({
          key: item.key,
          quantity: parseInt(item.count) || 1,
          price: parseFloat(item.price) || 0,
        }));
      if (calcItems.length === 0) { setCalcResult(null); return; }
      try {
        const res = await SpecialOrderService.calculate(selectedCountryId, { items: calcItems });
        if (res.data?.case === "success") {
          setCalcResult(res.data);
        } else if (res.data?.case === "warning" && res.data?.content) {
          setCalcResult(null);
          toast.error(res.data.content);
        }
      } catch {
        // silent
      }
    }, 600);
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: string) => {
    const updated = items.map((item, i) => i === index ? { ...item, [field]: value } : item);
    setItems(updated);
    if (field === "price" || field === "count") triggerCalculate(updated);
    if (errors[`items.${index}.${field}`]) {
      setErrors((prev) => { const n = { ...prev }; delete n[`items.${index}.${field}`]; return n; });
    }
  };

  const handleAddItem = () => {
    if (items.length >= 10) {
      toast.warning("Bir sifariş üçün maksimum 10 məhsul əlavə edə bilərsiniz.");
      return;
    }
    setItems((prev) => [...prev, emptyItem()]);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length === 1) return;
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    triggerCalculate(updated);
  };

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!phoneNumber.trim()) errs['phone'] = data?.required_field || "Mütləq doldurulmalıdır";
    items.forEach((item, i) => {
      if (!item.link.trim()) errs[`items.${i}.link`] = data?.required_field || "Mütləq doldurulmalıdır";
      if (!item.price || parseFloat(item.price) <= 0) errs[`items.${i}.price`] = data?.required_field || "Mütləq doldurulmalıdır";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCountryId || !validate()) return;
    setPendingOrderId(null);

    const minLimit = selectedCountry?.special_order_minimum_cost_limit ?? 0;
    if (minLimit > 0) {
      const orderTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.count) || 1), 0);
      if (orderTotal < minLimit) {
        toast.error(`Minimum sifariş məbləği ${minLimit} ${selectedCountry?.currency_name || ""} olmalıdır`);
        return;
      }
    }

    const MAX_AMOUNT = selectedCountry?.max_amount_local ?? null;
    const commission = calcResult?.commission_percent ?? serviceFeePercent ?? 0;
    const totalWithCommission = calcResult?.total_with_commission
      ?? items.reduce((sum, item) => {
        const subtotal = (parseFloat(item.price) || 0) * (parseInt(item.count) || 1);
        return sum + subtotal * (1 + commission / 100);
      }, 0);
    if (MAX_AMOUNT !== null && totalWithCommission > MAX_AMOUNT) {
      toast.error(`Maksimum sifariş məbləği ${MAX_AMOUNT} ${calcResult?.currency || selectedCountry?.currency_name || ""} ola bilər`);
      return;
    }

    setTermsConfirmMode(true);
    setTermsModalOpen(true);
  };

  const handleOpenTermsInfo = () => {
    setTermsConfirmMode(false);
    setTermsModalOpen(true);
  };

  const handleTermsConfirm = async () => {
    setTermsModalOpen(false);
    if (!selectedCountryId) return;

    if (isPartnerAccess && !isPaymentAccess) {
      setIsSubmitting(true);
      try {
        const payload = {
          country_id: selectedCountryId,
          url: items.map((i) => i.link),
          quantity: items.map((i) => parseInt(i.count) || 1),
          price: items.map((i) => parseFloat(i.price) || 0),
          color: items.map((i) => i.color || ""),
          size: items.map((i) => i.size || ""),
          description: items.map((i) => i.details || ""),
          phone_prefix: phonePrefix,
          phone1: phoneNumber,
        };
        const res = await SpecialOrderService.addOrder(selectedCountryId, payload);
        const rd = res?.data;
        if (rd?.case === "success" || res?.status === 200) {
          toast.success((data as any)?.order_sent || "Sifariş göndərildi");
          setItems([emptyItem()]);
          setCalcResult(null);
          fetchOrders(selectedCountryId);
        } else {
          const err = rd?.content || rd?.message || "";
          toast.error(typeof err === "string" && err ? err : (data?.error_occurred || "Xəta baş verdi"));
        }
      } catch (error: any) {
        const err = error?.response?.data?.content || error?.message || "";
        toast.error(typeof err === "string" && err ? err : (data?.error_occurred || "Xəta baş verdi"));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setPayMethodModal(true);
  };

  const handlePayMethodSelect = async (method: "balance" | "card") => {
    if (!selectedCountryId) return;

    if (method === "balance") {
      setIsSubmitting(true);
      try {
        const isExisting = pendingOrderId !== null;
        const payload = { payment_provider: "balance" };
        const res = isExisting
          ? await SpecialOrderService.payOrder(selectedCountryId, pendingOrderId!, payload)
          : await SpecialOrderService.addOrder(selectedCountryId, {
              ...payload,
              country_id: selectedCountryId,
              url: items.map((i) => i.link),
              quantity: items.map((i) => parseInt(i.count) || 1),
              price: items.map((i) => parseFloat(i.price) || 0),
              color: items.map((i) => i.color || ""),
              size: items.map((i) => i.size || ""),
              description: items.map((i) => i.details || ""),
              phone_prefix: phonePrefix,
              phone1: phoneNumber,
            });
        setPayMethodModal(false);
        const rd = res?.data;
        if (rd?.case === "success" || res?.status === 200) {
          setPaymentError("");
          setPaymentSuccess(true);
          if (rd?.used_azn !== undefined) {
            const debtAzn = parseFloat(rd?.debt_azn ?? "0");
            toast.info(
              debtAzn > 0
                ? `${data?.taken_from_balance || "Balansdan çıxıldı"}: ${rd.used_azn} AZN. ${data?.debt_created || "Borc yaradıldı"}: ${debtAzn.toFixed(2)} AZN`
                : `${data?.taken_from_balance || "Balansdan çıxıldı"}: ${rd.used_azn} AZN`
            );
          }
          if (!isExisting) { setItems([emptyItem()]); setCalcResult(null); }
          setPendingOrderId(null);
          fetchOrders(selectedCountryId);
          // Refresh balance
          SpecialOrderService.getBalance().then((r) => {
            const b = r.data?.balance ?? r.data?.data?.balance ?? null;
            const c = r.data?.currency ?? r.data?.data?.currency ?? "AZN";
            if (b !== null) { setBalance(String(b)); setBalanceCurrency(c); }
          });
        } else {
          const err = rd?.content || rd?.message || "";
          setPaymentError(typeof err === "string" ? err : "");
          setPaymentSuccess(false);
        }
        setIsPaymentStatusOpen(true);
      } catch (error: any) {
        setPayMethodModal(false);
        const err = error?.response?.data?.content || error?.response?.data?.message || error?.message || "";
        setPaymentError(typeof err === "string" ? err : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setPayMethodModal(false);
    // Card payment
    setIsSubmitting(true);
    try {
      const cardsRes = await BankCardsServices.getBankCards();
      const cardsList = cardsRes.data?.data || [];
      if (cardsList.length === 0) { setIsNoCardsModalOpen(true); return; }
      setCards(cardsList);
      setIsCardModalOpen(true);
    } catch {
      toast.error(data?.error_occurred || "Xəta baş verdi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardSelect = async (cardId: number) => {
    if (!selectedCountryId) return;
    setIsCardPaymentLoading(true);

    if (pendingOrderId === null) {
      // New order: include card_id in the add order body as arrays
      try {
        const payload = {
          payment_type: "card",
          card_id: cardId,
          country_id: selectedCountryId,
          url: items.map((i) => i.link),
          quantity: items.map((i) => parseInt(i.count) || 1),
          price: items.map((i) => parseFloat(i.price) || 0),
          color: items.map((i) => i.color || ""),
          size: items.map((i) => i.size || ""),
          description: items.map((i) => i.details || ""),
          phone_prefix: phonePrefix,
          phone1: phoneNumber,
        };
        const res = await SpecialOrderService.addOrder(selectedCountryId, payload);
        const responseData = res?.data;
        if (responseData?.case === "success" && responseData?.pay === true && responseData?.content) {
          window.location.href = responseData.content;
          return;
        }
        if (responseData?.case === "success" || res?.status === 200) {
          setPaymentError("");
          setPaymentSuccess(true);
          setItems([emptyItem()]);
          setCalcResult(null);
          fetchOrders(selectedCountryId);
        } else {
          const err = responseData?.content || responseData?.message || "";
          setPaymentError(typeof err === "string" ? err : "");
          setPaymentSuccess(false);
        }
        setIsPaymentStatusOpen(true);
      } catch (error: any) {
        const err = error?.content || error?.message || "";
        setPaymentError(typeof err === "string" ? err : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      } finally {
        setIsCardPaymentLoading(false);
        setIsCardModalOpen(false);
      }
    } else {
      // Existing order pay
      try {
        const res = await SpecialOrderService.payOrder(selectedCountryId, pendingOrderId, { payment_type: "card", card_id: cardId });
        const responseData = res?.data;
        if (responseData?.case === "success" && responseData?.pay === true && responseData?.content) {
          window.location.href = responseData.content;
          return;
        }
        if (responseData?.case === "success" || res?.status === 200) {
          setPaymentError("");
          setPaymentSuccess(true);
          setPendingOrderId(null);
          fetchOrders(selectedCountryId);
        } else {
          const err = responseData?.content || responseData?.message || "";
          setPaymentError(typeof err === "string" ? err : "");
          setPaymentSuccess(false);
        }
        setIsPaymentStatusOpen(true);
      } catch (error: any) {
        const err = error?.content || error?.message || "";
        setPaymentError(typeof err === "string" ? err : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      } finally {
        setIsCardPaymentLoading(false);
        setIsCardModalOpen(false);
      }
    }
  };

  const handlePaymentRedirect = () => {
    setIsPaymentStatusOpen(false);
    if (paymentSuccess && selectedCountryId) fetchOrders(selectedCountryId);
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!selectedCountryId) return;
    try {
      await SpecialOrderService.deleteOrder(selectedCountryId, orderId);
      toast.success(data?.deleted || "Silindi");
      fetchOrders(selectedCountryId);
    } catch {
      toast.error(data?.error_occurred || "Xəta baş verdi");
    }
  };

  const handlePayExistingOrder = async (orderId: number) => {
    setPendingOrderId(orderId);
    if (isPartnerAccess && !isPaymentAccess) return;
    setPayMethodModal(true);
  };

  const handlePartnerPay = (paymentLink: string) => {
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  };

  const triggerEditCalculate = (updatedItems: EditItem[]) => {
    if (!selectedCountryId) return;
    if (editCalculateTimer.current) clearTimeout(editCalculateTimer.current);
    editCalculateTimer.current = setTimeout(async () => {
      const hasData = updatedItems.some((i) => i.price && parseFloat(i.price) > 0);
      if (!hasData) { setEditCalcResult(null); return; }
      try {
        const calcItems = updatedItems.map((item) => ({
          key: String(item.id),
          quantity: parseInt(item.count) || 1,
          price: parseFloat(item.price) || 0,
          cargo_price: parseFloat(item.cargo_price) || 0,
        }));
        const res = await SpecialOrderService.calculate(selectedCountryId, { items: calcItems });
        if (res.data?.case === "success") setEditCalcResult(res.data);
      } catch { /* silent */ }
    }, 600);
  };

  const handleEditItemChange = (index: number, field: keyof EditItem, value: string) => {
    const updated = editItems.map((item, i) => i === index ? { ...item, [field]: value } : item);
    setEditItems(updated);
    if (field === "price" || field === "count" || field === "cargo_price") triggerEditCalculate(updated);
  };

  const handleOpenEdit = async (orderId: number) => {
    if (!selectedCountryId) return;
    setEditOrderId(orderId);
    setEditItems([]);
    setEditReason(null);
    setEditCalcResult(null);
    setEditLoading(true);
    setEditModalOpen(true);
    try {
      const res = await SpecialOrderService.getOrderForUpdate(selectedCountryId, orderId);
      if (res.data?.case === "success") {
        const loaded: EditItem[] = (res.data.orders || []).map((o: any) => ({
          id: o.id,
          url: o.url || "",
          price: String(o.price || ""),
          count: String(o.quantity || 1),
          color: o.color || "",
          size: o.size || "",
          description: o.description || "",
          cargo_price: o.cargo_price ? String(o.cargo_price) : "",
        }));
        setEditItems(loaded);
        setEditReason(res.data.edit_reason || null);
        triggerEditCalculate(loaded);
      } else {
        toast.error("Sifariş məlumatları yüklənmədi");
        setEditModalOpen(false);
      }
    } catch {
      toast.error("Xəta baş verdi");
      setEditModalOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedCountryId || !editOrderId || editSubmitting) return;
    setEditSubmitting(true);
    try {
      const payload = {
        order_id:    editItems.map((i) => i.id),
        url:         editItems.map((i) => i.url),
        quantity:    editItems.map((i) => parseInt(i.count) || 1),
        price:       editItems.map((i) => parseFloat(i.price) || 0),
        color:       editItems.map((i) => i.color || null),
        size:        editItems.map((i) => i.size || null),
        description: editItems.map((i) => i.description || null),
        cargo_price: editItems.map((i) => parseFloat(i.cargo_price) || 0),
      };
      const res = await SpecialOrderService.updatePartner(selectedCountryId, editOrderId, payload);
      if (res?.data?.case === "success" || res?.status === 200) {
        toast.success("Sifariş uğurla yeniləndi");
        setEditModalOpen(false);
        fetchOrders(selectedCountryId);
      } else {
        const err = res?.data?.content || res?.data?.message || "";
        toast.error(typeof err === "string" && err ? err : "Xəta baş verdi");
      }
    } catch (error: any) {
      const err = error?.content || error?.message || "";
      toast.error(typeof err === "string" && err ? err : "Xəta baş verdi");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCancelPartner = async () => {
    if (!selectedCountryId || !editOrderId || editCancelling) return;
    setEditCancelling(true);
    try {
      const res = await SpecialOrderService.cancelPartner(selectedCountryId, editOrderId);
      if (res?.data?.case === "success" || res?.status === 200) {
        toast.success("Sifariş ləğv edildi");
        setEditModalOpen(false);
        fetchOrders(selectedCountryId);
      } else {
        const err = res?.data?.content || res?.data?.message || "";
        toast.error(typeof err === "string" && err ? err : "Xəta baş verdi");
      }
    } catch (error: any) {
      const err = error?.content || error?.message || "";
      toast.error(typeof err === "string" && err ? err : "Xəta baş verdi");
    } finally {
      setEditCancelling(false);
    }
  };

  const handleViewGroupOrders = async (groupCode: string) => {
    if (!selectedCountryId) return;
    setGroupOrders([]);
    setGroupOrdersReason(null);
    setGroupOrdersLoading(true);
    setGroupOrdersModal(true);
    try {
      const res = await SpecialOrderService.showGroupOrders(selectedCountryId, groupCode);
      setGroupOrders(res.data?.orders || []);
      setGroupOrdersReason(res.data?.reason || null);
    } catch {
      setGroupOrders([]);
    } finally {
      setGroupOrdersLoading(false);
    }
  };

  const shortUrl = (urls: string) => {
    const first = urls.split(",")[0].trim();
    try {
      const host = new URL(first).hostname.replace("www.", "");
      return host;
    } catch {
      return first.length > 30 ? first.slice(0, 30) + "…" : first;
    }
  };

  const totalDebt = (order: SpecialOrder) => {
    const c = parseFloat(order.cargo_debt || "0");
    const d = parseFloat(order.common_debt || "0");
    return (c + d).toFixed(2);
  };

  const subText: CSSProperties = { fontSize: "0.75rem", color: "#888", marginTop: 2 };
  const balanceNum = parseFloat(balance ?? "0");

  // Balansla ödəniş bölgüsü: yeni sifarişdə calculate-dən, mövcud sifarişdə borcun AZN qarşılığından
  const balanceSplit = (() => {
    if (pendingOrderId === null) {
      const p = calcResult?.balance_preview;
      return p ? { from: p.from_balance_azn, debt: p.debt_azn } : null;
    }
    const order = orders.find((o) => o.id === pendingOrderId);
    const due = parseFloat(order?.total_amount_azn ?? "0");
    if (!order?.is_paid || !(due > 0)) return null;
    const from = Math.min(balanceNum, due);
    return { from, debt: due - from };
  })();

  const logTypeLabel = (log: BalanceLog) => {
    switch (log.type) {
      case "balance": return data?.log_order_payment || "Sifariş üçün istifadə";
      case "back": return data?.log_order_cancel || "Ləğv edilmiş sifariş — geri qaytarma";
      case "refund": return data?.log_cashier_refund || "Kassir tərəfindən geri ödəniş";
      case "card": return data?.log_card_refund || "Karta geri ödəniş";
      case "transfer": return data?.log_transfer || "Əsas balansa köçürmə";
      case "debt": return data?.log_debt_payment || "Borcun ödənilməsi";
      default: return data?.log_adjustment || "Balans düzəlişi";
    }
  };

  const logActorLabel = (actor?: string) => {
    if (actor === "client") return data?.log_actor_client || "Siz";
    if (actor === "staff") return data?.log_actor_staff || "Colibri əməkdaşı";
    return data?.log_actor_system || "Sistem";
  };

  return (
    <>
      <TitleUpdater
        title={data?.colibri ? `${data.colibri} | ${data?.special_order || "Sifariş et"}` : "Colibri"}
      />

      {/* Balance card */}
      {balance !== null && (
        <div className={styles.balanceCard}>
          <div className={styles.balanceInfo}>
            <span className={styles.balanceLabel}>Sifariş balansı</span>
            <span className={styles.balanceAmount}>{balance} {balanceCurrency}</span>
          </div>
          <button className={styles.logsBtn} type="button" onClick={handleOpenLogs}>
            {data?.balance_log || "Balans tarixçəsi"}
          </button>
        </div>
      )}

      {/* Country selector */}
      <div className={styles.countrySelector}>
        {countriesLoading ? (
          <div className={styles.countriesLoader}><Spinner size="small" /></div>
        ) : (
          <div className={styles.countriesStrip}>
            {countries.map((country) => (
              <button
                key={country.id}
                className={`${styles.countryBtn} ${selectedCountryId === country.id ? styles.countryBtnActive : ""} ${country.special_order_partner_access === 1 ? styles.countryBtnPartner : ""}`}
                type="button"
                onClick={() => handleSelectCountry(country.id)}
              >
                {(country.image || country.flag) && (
                  <img src={`${Base_Url}${country.image || country.flag}`} alt={country.country_name} />
                )}
                <span>{country.country_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {!countriesLoading && !selectedCountryId && countries.length > 0 && (
        <div className={styles.noCountryBox}>
          <p>{data?.special_order_select_country_hint || "Sifariş vermək üçün əvvəlcə ölkə seçin"}</p>
          <button type="button" className={styles.submitBtn} onClick={() => setCountryModalOpen(true)}>
            {data?.select_country || "Ölkə seçin"}
          </button>
        </div>
      )}

      {selectedCountryId && (
        <>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.formTitleRow}>
              <h2 className={styles.formTitle}>{data?.special_order || "Sifariş et"}</h2>
              <button
                type="button"
                className={styles.termsInfoBtn}
                onClick={handleOpenTermsInfo}
                aria-label={data?.special_order_terms_title || "Sifariş qaydaları"}
                title={data?.special_order_terms_title || "Sifariş qaydaları"}
              >
                i
              </button>
            </div>

            <div className={styles.phoneRow}>
              <div className={styles.field}>
                <label>Kod</label>
                <select
                  className={styles.phoneSelect}
                  value={phonePrefix}
                  onChange={(e) => setPhonePrefix(e.target.value)}
                >
                  {phonePrefixes.map((p) => (
                    <option key={p.id} value={p.prefix}>{p.prefix}</option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>{data?.phone || "Əlaqə nömrəsi"}</label>
                <input
                  type="text"
                  maxLength={7}
                  placeholder="0000000"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                  className={errors["phone"] ? styles.inputError : ""}
                />
                {errors["phone"] && <span className={styles.error}>{errors["phone"]}</span>}
              </div>
            </div>

            {items.map((item, index) => (
              <div key={item.key} className={styles.itemBlock}>
                {items.length > 1 && (
                  <div className={styles.itemHeader}>
                    <span className={styles.itemNum}>{index + 1}.</span>
                    <button
                      type="button"
                      className={styles.removeItemBtn}
                      onClick={() => handleRemoveItem(index)}
                    >
                      <img src={deleteBtn} alt="sil" />
                    </button>
                  </div>
                )}

                <div className={styles.row3}>
                  <div className={styles.field}>
                    <label>{data?.product_link || "Məhsulun linki"}</label>
                    <input
                      type="text"
                      placeholder={data?.product_link || "Məhsulun linkini daxil et"}
                      value={item.link}
                      onChange={(e) => handleItemChange(index, "link", e.target.value)}
                      className={errors[`items.${index}.link`] ? styles.inputError : ""}
                    />
                    {errors[`items.${index}.link`] && <span className={styles.error}>{errors[`items.${index}.link`]}</span>}
                  </div>

                  <div className={styles.field}>
                    <label>
                      {data?.product_price || "Məhsulun qiyməti"}
                      {countries.find(c => c.id === selectedCountryId)?.currency_name && (
                        <span className={styles.feeTag}> ({countries.find(c => c.id === selectedCountryId)?.currency_name})</span>
                      )}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={item.price}
                      onChange={(e) => handleItemChange(index, "price", e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className={errors[`items.${index}.price`] ? styles.inputError : ""}
                    />
                    {errors[`items.${index}.price`] && <span className={styles.error}>{errors[`items.${index}.price`]}</span>}
                  </div>

                  <div className={styles.field}>
                    <label>
                      {data?.service_fee || "Məhsulun xidmət faizi"}
                      {(calcResult?.commission_percent ?? serviceFeePercent) !== null && (
                        <span className={styles.feeTag}> (+{calcResult?.commission_percent ?? serviceFeePercent}%)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      readOnly
                      disabled
                      placeholder="0.00"
                      value={calcResult?.items?.find((ci) => ci.key === item.key)?.subtotal_with_commission ?? ""}
                      className={styles.readonlyInput}
                    />
                  </div>
                </div>

                <div className={styles.row3}>
                  <div className={styles.field}>
                    <label>{data?.product_count || "Məhsulun sayı"}</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="1"
                      value={item.count}
                      onChange={(e) => handleItemChange(index, "count", e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>

                  <div className={styles.field}>
                    <label>{data?.color || "Rəng"}</label>
                    <input
                      type="text"
                      placeholder={data?.color_placeholder || "Rəngi qeyd et"}
                      value={item.color}
                      onChange={(e) => handleItemChange(index, "color", e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label>{data?.size || "Ölçü"}</label>
                    <input
                      type="text"
                      placeholder="o"
                      value={item.size}
                      onChange={(e) => handleItemChange(index, "size", e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.field}>
                  <label>{data?.product_details || "Məhsulun detalları"}</label>
                  <textarea
                    rows={3}
                    placeholder={data?.product_details_placeholder || "Məhsulun detalları..."}
                    value={item.details}
                    onChange={(e) => handleItemChange(index, "details", e.target.value)}
                  />
                </div>
              </div>
            ))}

            <div className={styles.formFooter}>
              <button
                type="button"
                className={styles.addItemBtn}
                onClick={handleAddItem}
                disabled={items.length >= 10}
              >
                + {data?.add_product || "Yeni məhsul əlavə et"}
              </button>

              {isPartnerAccess && calcResult && calcResult.spain_cargo_price !== null && calcResult.spain_cargo_price !== undefined && (
                <div className={styles.field}>
                  <label>{data?.spain_delivery_fee || "Daxili karqo məbləği"}</label>
                  <input
                    type="text"
                    readOnly
                    disabled
                    value={`${calcResult.spain_cargo_price} ${calcResult.currency}`}
                    className={styles.readonlyInput}
                  />
                </div>
              )}
              <div className={styles.submitArea}>
                <div className={styles.total}>
                  <span>{data?.total_amount || "Yekun Məbləğ"}</span>
                  <strong>
                    {calcResult
                      ? isPartnerAccess
                        ? `${calcResult.total_with_commission} ${calcResult.currency}`
                        : `${calcResult.total_with_commission_azn} ${calcResult.currency_azn}`
                      : "0.00"}
                  </strong>
                </div>
                <button type="submit" className={styles.submitBtn} disabled={isSubmitting || !phoneNumber.trim()}>
                  {isSubmitting ? <Spinner size="small" /> : (data?.confirm || "Təsdiqlə")}
                </button>
              </div>
            </div>
          </form>

          {/* Order history */}
          <div className={styles.historySection}>
            <div className={styles.pagination_part}>
              <p className={styles.pagination_title}>{data?.order_history || "Sifarişlərim"}</p>
              <div className={styles.archiveFilter}>
                <SelectComponent
                  name="archive"
                  options={[
                    { label: data?.special_order_current || "Mövcud", value: "no" },
                    { label: data?.special_order_all || "Hamısı", value: "yes" },
                  ]}
                  selectedValue={archiveFilter ?? "no"}
                  value={archiveFilter ?? "no"}
                  onChange={(val) => setArchiveFilter(val as "yes" | "no")}
                />
              </div>
              {!(currentPage === 1 && lastPage === 1) && (
                <div className={styles.pagination}>
                  <img
                    src={ArrowImage}
                    className={`${styles.pagination_arrow_left} ${currentPage === 1 ? styles.disabled : ""}`}
                    alt="Prev"
                    onClick={() => { if (currentPage > 1 && selectedCountryId) fetchOrders(selectedCountryId, currentPage - 1); }}
                  />
                  {(() => {
                    const pages: (number | "...")[] = [];
                    if (lastPage <= 7) {
                      for (let i = 1; i <= lastPage; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (currentPage > 3) pages.push("...");
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(lastPage - 1, currentPage + 1); i++) pages.push(i);
                      if (currentPage < lastPage - 2) pages.push("...");
                      pages.push(lastPage);
                    }
                    return pages.map((page, i) =>
                      page === "..." ? (
                        <span key={`dots-${i}`} className={styles.pagination_dots}>…</span>
                      ) : (
                        <p
                          key={page}
                          className={`${styles.pagination_count} ${currentPage === page ? styles.active : ""}`}
                          onClick={() => { if (selectedCountryId) fetchOrders(selectedCountryId, page); }}
                        >
                          {page}
                        </p>
                      )
                    );
                  })()}
                  <img
                    src={ArrowImage}
                    className={`${styles.pagination_arrow_right} ${currentPage === lastPage ? styles.disabled : ""}`}
                    alt="Next"
                    onClick={() => { if (currentPage < lastPage && selectedCountryId) fetchOrders(selectedCountryId, currentPage + 1); }}
                  />
                </div>
              )}
            </div>

            {ordersLoading ? (
              <div className={styles.loader}><Spinner size="large" /></div>
            ) : orders.length === 0 ? (
              <p className={styles.emptyText}>{data?.no_data || "Sifariş yoxdur"}</p>
            ) : (
              <>
                {/* Desktop table */}
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>URL</th>
                      <th>{data?.product_price || "Qiymət"}</th>
                      <th>{data?.debt || "Borc"}</th>
                      <th>{data?.status || "Status"}</th>
                      <th>{data?.total_amount || "Məbləğ"}</th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td>
                          <div>{order.id}</div>
                          {isPartnerAccess && order.orderNumber && (
                            <div style={{ fontSize: "0.75rem", color: "#888", marginTop: 2 }}>{order.orderNumber}</div>
                          )}
                        </td>
                        <td>
                          {order.urls ? (
                            <a
                              href={order.urls.split(",")[0].trim()}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.urlLink}
                            >
                              {shortUrl(order.urls)}
                            </a>
                          ) : "-"}
                        </td>
                        <td>
                          <div>{order.price || "-"} {order.currency || ""}</div>
                          {parseFloat(order.price_azn || "0") > 0 && (
                            <div style={subText}>{data?.paid || "Ödənilib"}: {order.price_azn} AZN</div>
                          )}
                        </td>
                        <td>
                          <div>{totalDebt(order)} {order.currency || ""}</div>
                          {parseFloat(totalDebt(order)) > 0 && order.total_amount_azn && (
                            <div style={subText}>≈ {order.total_amount_azn} AZN</div>
                          )}
                        </td>
                        <td>
                          <span className={order.is_paid ? styles.badgePaid : styles.badgeUnpaid}>
                            {order.status || "-"}
                          </span>
                        </td>
                        <td>{order.total_amount || "-"} {order.currency || ""}</td>
                        <td>
                          {isPartnerAccess ? (
                            !order.is_paid && !order.waiting_for_payment && (
                              <button
                                className={`${styles.payBtn} ${!order.special_order_payment_link ? styles.payBtnPending : ""}`}
                                onClick={() => order.special_order_payment_link && handlePartnerPay(order.special_order_payment_link)}
                                disabled={!order.special_order_payment_link}
                              >
                                {data?.pay || "Ödə"}
                              </button>
                            )
                          ) : (
                            ((!order.is_paid || parseFloat(order.cargo_debt || "0") > 0 || parseFloat(order.common_debt || "0") > 0) && !order.waiting_for_payment) && (
                              <button
                                className={styles.payBtn}
                                onClick={() => handlePayExistingOrder(order.id)}
                              >
                                {data?.pay || "Ödə"}
                              </button>
                            )
                          )}
                        </td>
                        <td>
                          {order.group_code && (
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleViewGroupOrders(order.group_code!)}
                            >
                              <img src={eyeIcon} alt="bax" style={{ width: 18 }} />
                            </button>
                          )}
                        </td>
                        <td>
                          {!isPartnerAccess && !order.is_paid && (
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteOrder(order.id)}
                            >
                              <img src={deleteBtn} alt="sil" style={{ width: 18 }} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <div className={styles.mobileCards}>
                  {orders.map((order) => (
                    <div key={order.id} className={styles.mobileCard}>
                      <div className={styles.mobileCardRow}>
                        <span className={styles.mobileLabel}>ID</span>
                        <span>
                          {order.id}
                          {isPartnerAccess && order.orderNumber && (
                            <span style={{ fontSize: "0.75rem", color: "#888", marginLeft: 6 }}>{order.orderNumber}</span>
                          )}
                        </span>
                      </div>
                      {order.urls && (
                        <div className={styles.mobileCardRow}>
                          <span className={styles.mobileLabel}>URL</span>
                          <a
                            href={order.urls.split(",")[0].trim()}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.urlLink}
                          >
                            {shortUrl(order.urls)}
                          </a>
                        </div>
                      )}
                      <div className={styles.mobileCardRow}>
                        <span className={styles.mobileLabel}>{data?.product_price || "Qiymət"}</span>
                        <span>
                          {order.price || "-"} {order.currency || ""}
                          {parseFloat(order.price_azn || "0") > 0 && (
                            <div style={subText}>{data?.paid || "Ödənilib"}: {order.price_azn} AZN</div>
                          )}
                        </span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span className={styles.mobileLabel}>{data?.debt || "Borc"}</span>
                        <span>
                          {totalDebt(order)} {order.currency || ""}
                          {parseFloat(totalDebt(order)) > 0 && order.total_amount_azn && (
                            <div style={subText}>≈ {order.total_amount_azn} AZN</div>
                          )}
                        </span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span className={styles.mobileLabel}>{data?.status || "Status"}</span>
                        <span className={order.is_paid ? styles.badgePaid : styles.badgeUnpaid}>
                          {order.status || "-"}
                        </span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span className={styles.mobileLabel}>{data?.total_amount || "Məbləğ"}</span>
                        <span>{order.total_amount || "-"} {order.currency || ""}</span>
                      </div>
                      <div className={styles.mobileCardActions}>
                        {isPartnerAccess ? (
                          !order.is_paid && !order.waiting_for_payment && (
                            <button
                              className={`${styles.payBtn} ${!order.special_order_payment_link ? styles.payBtnPending : ""}`}
                              onClick={() => order.special_order_payment_link && handlePartnerPay(order.special_order_payment_link)}
                              disabled={!order.special_order_payment_link}
                            >
                              {data?.pay || "Ödə"}
                            </button>
                          )
                        ) : (
                          ((!order.is_paid || parseFloat(order.cargo_debt || "0") > 0 || parseFloat(order.common_debt || "0") > 0) && !order.waiting_for_payment) && (
                            <button
                              className={styles.payBtn}
                              onClick={() => handlePayExistingOrder(order.id)}
                            >
                              {data?.pay || "Ödə"}
                            </button>
                          )
                        )}
                        {order.group_code && (
                          <button
                            className={styles.deleteBtn}
                            onClick={() => handleViewGroupOrders(order.group_code!)}
                          >
                            <img src={eyeIcon} alt="bax" style={{ width: 18 }} />
                          </button>
                        )}
                        {!isPartnerAccess && !order.is_paid && (
                          <button
                            className={styles.deleteBtn}
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            <img src={deleteBtn} alt="sil" style={{ width: 18 }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      <CardSelectModal
        isOpen={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
        onSelectCard={handleCardSelect}
        cards={cards}
        isLoading={isCardPaymentLoading}
        translationData={data}
      />

      <PaymentStatusModal
        isOpen={isPaymentStatusOpen}
        isSuccess={paymentSuccess}
        onClose={() => setIsPaymentStatusOpen(false)}
        onRedirect={handlePaymentRedirect}
        translationData={data}
        errorMessage={paymentError}
      />

      <NoCardsModal
        isOpen={isNoCardsModalOpen}
        onClose={() => setIsNoCardsModalOpen(false)}
        translationData={data}
      />

      {/* Country select modal */}
      <GlobalModalComponent
        isOpen={countryModalOpen}
        onClose={() => setCountryModalOpen(false)}
      >
        <div className={styles.countryModalBox}>
          <div className={`${styles.modalHeader} ${styles.countryModalHeader}`}>
            <div>
              <span className={styles.modalTitle}>{data?.select_country || "Ölkə seçin"}</span>
              <p className={styles.countryModalSub}>
                {data?.special_order_select_country_hint || "Sifariş vermək üçün əvvəlcə ölkə seçin"}
              </p>
            </div>
            <button
              type="button"
              className={styles.modalClose}
              aria-label={data?.close || "Bağla"}
              onClick={() => setCountryModalOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className={styles.countryModalGrid}>
            {countries.map((country) => (
              <button
                key={country.id}
                type="button"
                className={`${styles.countryCard} ${selectedCountryId === country.id ? styles.countryCardActive : ""} ${country.special_order_partner_access === 1 ? styles.countryCardPartner : ""}`}
                onClick={() => handleSelectCountry(country.id)}
              >
                {country.image || country.flag ? (
                  <img
                    src={`${Base_Url}${country.image || country.flag}`}
                    alt={country.country_name}
                    className={styles.countryCardFlag}
                  />
                ) : (
                  <div className={styles.countryCardFlagPlaceholder}>
                    <span>{country.country_name?.charAt(0)}</span>
                  </div>
                )}
                <span className={styles.countryCardName}>{country.country_name}</span>
                {country.currency_name && (
                  <span className={styles.countryCardCurrency}>{country.currency_name}</span>
                )}
              </button>
            ))}
          </div>
          <div className={styles.countryModalFooter}>
            <button type="button" className={styles.countryBackBtn} onClick={handleCountryModalBack}>
              ← {data?.go_back || "Geri qayıt"}
            </button>
          </div>
        </div>
      </GlobalModalComponent>

      {/* Payment method modal */}
      <GlobalModalComponent isOpen={payMethodModal} onClose={() => !isSubmitting && setPayMethodModal(false)}>
        <div className={styles.modalBox} style={{ position: "relative" }}>
          {isSubmitting && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.85)", borderRadius: "inherit", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
              <Spinner size="large" />
            </div>
          )}
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>Ödəniş üsulu</span>
            <button className={styles.modalClose} onClick={() => !isSubmitting && setPayMethodModal(false)}>✕</button>
          </div>
          <div className={styles.payMethodOptions}>
            <button
              className={`${styles.payMethodBtn} ${styles.payMethodBtnStacked}`}
              onClick={() => handlePayMethodSelect("balance")}
              disabled={isSubmitting || !(balanceNum > 0)}
            >
              <span className={styles.payMethodHead}>
                <span className={styles.payMethodName}>{data?.pay_from_balance || "Balansla ödə"}</span>
                {balance !== null && (
                  <span className={styles.payMethodBalance}>
                    {data?.your_balance || "Balansınız"}: {balance} {balanceCurrency}
                  </span>
                )}
              </span>

              {balanceNum > 0 && balanceSplit && (
                <span className={styles.payBreakdown}>
                  <span className={styles.payRow}>
                    <span>{data?.order_amount || "Sifariş məbləği"}</span>
                    <b>{(balanceSplit.from + balanceSplit.debt).toFixed(2)} AZN</b>
                  </span>
                  <span className={`${styles.payRow} ${styles.payRowPaid}`}>
                    <span>{data?.paid_from_balance || "Balansdan ödənilir"}</span>
                    <b>− {balanceSplit.from.toFixed(2)} AZN</b>
                  </span>
                  {balanceSplit.debt > 0 && (
                    <span className={`${styles.payRow} ${styles.payRowDebt}`}>
                      <span>{data?.remains_as_debt || "Borc olaraq qalır"}</span>
                      <b>{balanceSplit.debt.toFixed(2)} AZN</b>
                    </span>
                  )}
                  <span className={styles.payRow}>
                    <span>{data?.balance_after_payment || "Ödənişdən sonra balans"}</span>
                    <b>{Math.max(0, balanceNum - balanceSplit.from).toFixed(2)} AZN</b>
                  </span>
                  {balanceSplit.debt > 0 && (
                    <span className={styles.payNote}>
                      {data?.debt_note ||
                        "Balansınız sifarişin tam məbləğini ödəmək üçün kifayət etmir. Çatışmayan məbləğ sifarişinizə borc kimi yazılacaq, onu sonradan ödəyə bilərsiniz."}
                    </span>
                  )}
                </span>
              )}
            </button>
            <button className={styles.payMethodBtn} onClick={() => handlePayMethodSelect("card")} disabled={isSubmitting}>
              <span className={styles.payMethodName}>{data?.pay_by_card || "Kartla ödə"}</span>
            </button>
          </div>
        </div>
      </GlobalModalComponent>

      {/* Terms modal */}
      <GlobalModalComponent isOpen={termsModalOpen} onClose={() => setTermsModalOpen(false)}>
        <div className={styles.termsModalBox}>
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{data?.special_order_terms_title || "Sifariş qaydaları"}</span>
            <button className={styles.modalClose} onClick={() => setTermsModalOpen(false)}>✕</button>
          </div>
          <div
            className={styles.termsModalBody}
            dangerouslySetInnerHTML={{ __html: data?.special_order_terms_content || "" }}
          />
          {termsConfirmMode && (
            <div className={styles.termsModalFooter}>
              <button
                type="button"
                className={styles.submitBtn}
                disabled={isSubmitting}
                onClick={handleTermsConfirm}
              >
                {isSubmitting ? <Spinner size="small" /> : (data?.special_order_terms_confirm || "Qaydalarla tanış oldum")}
              </button>
            </div>
          )}
        </div>
      </GlobalModalComponent>

      {/* Partner edit modal */}
      <GlobalModalComponent isOpen={editModalOpen} onClose={() => !editSubmitting && setEditModalOpen(false)}>
        <div className={styles.editModalBox} style={{ position: "relative" }}>
          {editSubmitting && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.85)", borderRadius: "inherit", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
              <Spinner size="large" />
            </div>
          )}
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>Sifarişi düzəlt</span>
            <button className={styles.modalClose} onClick={() => !editSubmitting && setEditModalOpen(false)}>✕</button>
          </div>

          {editReason && (
            <div className={styles.editReasonBanner}>
              <strong>Düzəliş səbəbi:</strong> {editReason}
            </div>
          )}

          <div className={styles.editFormBody}>
            {editLoading ? (
              <div className={styles.loader}><Spinner size="large" /></div>
            ) : (
              <>
                {editItems.map((item, index) => (
                  <div key={item.id} className={styles.itemBlock}>
                    {editItems.length > 1 && (
                      <div className={styles.itemHeader}>
                        <span className={styles.itemNum}>{index + 1}.</span>
                      </div>
                    )}

                    <div className={styles.row3}>
                      <div className={styles.field}>
                        <label>{data?.product_link || "Məhsulun linki"}</label>
                        <input
                          type="text"
                          value={item.url}
                          onChange={(e) => handleEditItemChange(index, "url", e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      <div className={styles.field}>
                        <label>
                          {data?.product_price || "Qiymət"}
                          {selectedCountry?.currency_name && (
                            <span className={styles.feeTag}> ({selectedCountry.currency_name})</span>
                          )}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => handleEditItemChange(index, "price", e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="0.00"
                        />
                      </div>
                      <div className={styles.field}>
                        <label>
                          {data?.service_fee || "Komissiya ilə"}
                          {editCalcResult?.commission_percent != null && (
                            <span className={styles.feeTag}> (+{editCalcResult.commission_percent}%)</span>
                          )}
                        </label>
                        <input
                          type="text"
                          readOnly
                          disabled
                          placeholder="0.00"
                          value={editCalcResult?.items?.find((ci) => ci.key === String(item.id))?.subtotal_with_commission ?? ""}
                          className={styles.readonlyInput}
                        />
                      </div>
                    </div>

                    <div className={styles.row3}>
                      <div className={styles.field}>
                        <label>{data?.product_count || "Say"}</label>
                        <input
                          type="number"
                          min="1"
                          value={item.count}
                          onChange={(e) => handleEditItemChange(index, "count", e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="1"
                        />
                      </div>
                      <div className={styles.field}>
                        <label>{data?.color || "Rəng"}</label>
                        <input
                          type="text"
                          value={item.color}
                          onChange={(e) => handleEditItemChange(index, "color", e.target.value)}
                          placeholder={data?.color_placeholder || "Rəngi qeyd et"}
                        />
                      </div>
                      <div className={styles.field}>
                        <label>{data?.size || "Ölçü"}</label>
                        <input
                          type="text"
                          value={item.size}
                          onChange={(e) => handleEditItemChange(index, "size", e.target.value)}
                          placeholder="M / L / XL"
                        />
                      </div>
                    </div>

                    <div className={styles.row3}>
                      <div className={styles.field}>
                        <label>
                          Kargo qiyməti
                          {selectedCountry?.currency_name && (
                            <span className={styles.feeTag}> ({selectedCountry.currency_name})</span>
                          )}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.cargo_price}
                          onChange={(e) => handleEditItemChange(index, "cargo_price", e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className={styles.field}>
                      <label>{data?.product_details || "Detallar"}</label>
                      <textarea
                        rows={2}
                        value={item.description}
                        onChange={(e) => handleEditItemChange(index, "description", e.target.value)}
                        placeholder={data?.product_details_placeholder || "Məhsulun detalları..."}
                      />
                    </div>
                  </div>
                ))}

                <div className={`${styles.submitArea} ${styles.editSubmitArea}`}>
                  <div className={styles.total}>
                    <span>{data?.total_amount || "Yekun məbləğ"}</span>
                    <strong>
                      {editCalcResult
                        ? `${editCalcResult.total_with_commission} ${editCalcResult.currency}`
                        : "—"}
                    </strong>
                  </div>
                  <div className={styles.editActionBtns}>
                    <button
                      className={styles.submitBtn}
                      onClick={handleSubmitEdit}
                      disabled={editSubmitting || editLoading || editCancelling}
                    >
                      {editSubmitting ? <Spinner size="small" /> : "Təsdiqlə"}
                    </button>
                    <button
                      className={styles.cancelBtn}
                      onClick={() => setCancelConfirmOpen(true)}
                      disabled={editSubmitting || editLoading || editCancelling}
                    >
                      {editCancelling ? <Spinner size="small" /> : "İmtina"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </GlobalModalComponent>

      {/* Cancel confirm modal */}
      <GlobalModalComponent isOpen={cancelConfirmOpen} onClose={() => !editCancelling && setCancelConfirmOpen(false)}>
        <div className={styles.confirmModal}>
          <div className={styles.confirmIcon}>⚠️</div>
          <p className={styles.confirmTitle}>Sifarişdən imtina</p>
          <p className={styles.confirmText}>Bu sifarişdən imtina etmək istədiyinizə əminsiniz? Bu əməliyyat geri alına bilməz.</p>
          <div className={styles.confirmActions}>
            <button
              className={styles.cancelBtn}
              onClick={async () => {
                setCancelConfirmOpen(false);
                await handleCancelPartner();
              }}
              disabled={editCancelling}
            >
              {editCancelling ? <Spinner size="small" /> : "Bəli, imtina et"}
            </button>
            <button
              className={styles.submitBtn}
              onClick={() => setCancelConfirmOpen(false)}
              disabled={editCancelling}
            >
              Xeyr, qal
            </button>
          </div>
        </div>
      </GlobalModalComponent>

      {/* Logs modal */}
      <GlobalModalComponent isOpen={logsModal} onClose={() => setLogsModal(false)}>
        <div className={styles.modalBox}>
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{data?.balance_log || "Balans tarixçəsi"}</span>
            <button className={styles.modalClose} onClick={() => setLogsModal(false)}>✕</button>
          </div>
          <div className={styles.groupOrdersList}>
            {logsLoading ? (
              <div className={styles.loader}><Spinner size="large" /></div>
            ) : logs.length === 0 ? (
              <p className={styles.emptyText}>Log yoxdur</p>
            ) : (
              logs.map((log) => {
                const isIn = log.status === "in";
                const hasAzn = log.amount_azn !== null && log.amount_azn !== undefined;
                return (
                  <div key={log.id} className={styles.groupOrderItem}>
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.auth_courier_date || "Tarix"}</span>
                      <span>{log.created_at ? formatDate(log.created_at) : "-"}</span>
                    </div>
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.type || "Əməliyyat"}</span>
                      <span className={isIn ? styles.badgePaid : styles.badgeUnpaid}>{logTypeLabel(log)}</span>
                    </div>
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.total_amount || "Məbləğ"}</span>
                      <span>
                        {isIn ? "+" : "−"}{hasAzn ? `${log.amount_azn} AZN` : `${log.amount} ${log.currency || ""}`}
                        {hasAzn && log.currency && log.currency !== "AZN" && (
                          <div style={subText}>
                            {log.amount} {log.currency}
                            {log.rate && ` · 1 ${log.currency} = ${parseFloat(log.rate)} AZN`}
                            {log.rate_date && ` (${log.rate_date})`}
                          </div>
                        )}
                      </span>
                    </div>
                    {log.balance_before !== null && log.balance_before !== undefined && (
                      <div className={styles.groupOrderRow}>
                        <span className={styles.groupOrderLabel}>{data?.balance_change || "Balans"}</span>
                        <span>{log.balance_before} → {log.balance_after} AZN</span>
                      </div>
                    )}
                    {log.order_group_id && (
                      <div className={styles.groupOrderRow}>
                        <span className={styles.groupOrderLabel}>{data?.order_number || "Sifariş"}</span>
                        <span>#{log.order_group_id}{log.order_number ? ` · ${log.order_number}` : ""}</span>
                      </div>
                    )}
                    {log.reason && (
                      <div className={styles.groupOrderRow}>
                        <span className={styles.groupOrderLabel}>{data?.reason || "Səbəb"}</span>
                        <span>{log.reason}</span>
                      </div>
                    )}
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.performed_by || "İcra edən"}</span>
                      <span>{logActorLabel(log.actor)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </GlobalModalComponent>

      {/* Group orders modal */}
      <GlobalModalComponent isOpen={groupOrdersModal} onClose={() => setGroupOrdersModal(false)}>
        <div className={styles.modalBox}>
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{data?.order_history || "Sifarişlər"}</span>
            <button className={styles.modalClose} onClick={() => setGroupOrdersModal(false)}>✕</button>
          </div>
          {groupOrdersReason && (
            <div className={styles.editReasonBanner}>
              <strong>Səbəb:</strong> {groupOrdersReason}
            </div>
          )}
          <div className={styles.groupOrdersList}>
          {groupOrdersLoading ? (
            <div className={styles.loader}><Spinner size="large" /></div>
          ) : groupOrders.length === 0 ? (
            <p className={styles.emptyText}>{data?.no_data || "Məlumat yoxdur"}</p>
          ) : (
            <>
              {groupOrders.map((item, i) => (
                <div key={i} className={styles.groupOrderItem}>
                  <div className={styles.groupOrderRow}>
                    <span className={styles.groupOrderLabel}>URL</span>
                    <a href={item.url} target="_blank" rel="noreferrer" className={styles.urlLink}>
                      {(() => { try { return new URL(item.url).hostname.replace("www.", ""); } catch { return item.url.slice(0, 40); } })()}
                    </a>
                  </div>
                  <div className={styles.groupOrderRow}>
                    <span className={styles.groupOrderLabel}>{data?.product_price || "Qiymət"}</span>
                    <span>{item.price}</span>
                  </div>
                  <div className={styles.groupOrderRow}>
                    <span className={styles.groupOrderLabel}>{data?.product_count || "Say"}</span>
                    <span>{item.quantity}</span>
                  </div>
                  {item.cargo_price !== null && item.cargo_price !== undefined && (
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.spain_delivery_fee || "Daxili karqo məbləği"}</span>
                      <span>{Number(item.cargo_price).toFixed(2)} EUR</span>
                    </div>
                  )}
                  {item.description && (
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.product_details || "Detal"}</span>
                      <span>{item.description}</span>
                    </div>
                  )}
                  {item.status && (
                    <div className={styles.groupOrderRow}>
                      <span className={styles.groupOrderLabel}>{data?.status || "Status"}</span>
                      <span className={styles.badgeUnpaid}>{item.status}</span>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          </div>
        </div>
      </GlobalModalComponent>
    </>
  );
};

export default SpecialOrder;
